import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import OrgWorkspaceHeader from "@/components/admin/organization/OrgWorkspaceHeader";
import OrgWorkspaceTabs from "@/components/admin/organization/OrgWorkspaceTabs";
import OrgRightPanel from "@/components/admin/organization/OrgRightPanel";
import OrgSetupTab from "@/components/admin/organization/OrgSetupTab";
import OrgUsersTab from "@/components/admin/organization/OrgUsersTab";
import OrgSolutionsTab from "@/components/admin/organization/OrgSolutionsTab";
import OrgBillingTab from "@/components/admin/organization/OrgBillingTab";
import OrgChecklistTab from "@/components/admin/organization/OrgChecklistTab";

export default function AdminOrganizationWorkspace() {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const { API } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("org-setup");
  const [activating, setActivating] = useState(false);

  const load = useCallback(async () => {
    setError("");
    try {
      const { data: resp } = await axios.get(`${API}/admin/organizations/${orgId}`, { withCredentials: true });
      setData(resp);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  }, [API, orgId]);

  useEffect(() => { load(); }, [load]);

  const applySaved = (resp) => {
    setData((current) => ({
      ...current,
      organization: resp.organization,
      requirements: resp.requirements || current.requirements,
      owner_invitation_status: resp.owner_invitation_status || current.owner_invitation_status,
      limits: resp.limits || current.limits,
      usage: resp.usage || current.usage,
    }));
  };

  const changeStatus = async (status) => {
    setError("");
    try {
      const { data: resp } = await axios.patch(`${API}/admin/organizations/${orgId}/status`, { status }, { withCredentials: true });
      applySaved(resp);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const activate = async () => {
    setActivating(true);
    try {
      await changeStatus("Active");
    } finally {
      setActivating(false);
    }
  };

  if (error && !data) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-sm font-semibold text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-400" data-testid="org-workspace-error">
        {error}
        <button type="button" onClick={() => navigate("/admin/organizations")} data-testid="org-workspace-error-back" className="ml-3 underline">Back to Organizations</button>
      </div>
    );
  }

  if (!data) {
    return <div className="rounded-xl border border-slate-200 bg-slate-50 p-10 text-center text-sm font-semibold text-slate-500 dark:border-white/10 dark:bg-white/[0.03] dark:text-slate-400" data-testid="org-workspace-loading">Loading organization…</div>;
  }

  const { organization, requirements, owner_invitation_status, limits, usage, quote, users, memberships, invitations } = data;

  return (
    <div className="space-y-6" data-testid="org-workspace-page">
      <OrgWorkspaceHeader org={organization} requirements={requirements} onStatusChange={changeStatus} />
      {error && <p className="rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 dark:bg-red-500/10 dark:text-red-400" data-testid="org-workspace-inline-error">{error}</p>}
      <OrgWorkspaceTabs active={tab} onChange={setTab} />
      <div className="grid gap-6 lg:grid-cols-[7fr_3fr]" key={organization.org_id}>
        <div className="min-w-0">
          {tab === "org-setup" && <OrgSetupTab org={organization} API={API} onSaved={applySaved} />}
          {tab === "users" && (
            <OrgUsersTab
              org={organization}
              ownerInvitationStatus={owner_invitation_status}
              limits={limits}
              usage={usage}
              users={users}
              memberships={memberships}
              invitations={invitations}
              API={API}
              onSaved={applySaved}
              reload={load}
            />
          )}
          {tab === "solutions" && <OrgSolutionsTab org={organization} catalog={data.solutions_catalog || []} API={API} onSaved={load} onGoToOrgSetup={() => setTab("org-setup")} />}
          {tab === "billing" && <OrgBillingTab org={organization} quote={quote} API={API} onSaved={applySaved} />}
          {tab === "checklist" && <OrgChecklistTab requirements={requirements} org={organization} onActivate={activate} activating={activating} />}
        </div>
        <div className="lg:sticky lg:top-24 lg:self-start">
          <OrgRightPanel
            org={organization}
            requirements={requirements}
            ownerInvitationStatus={owner_invitation_status}
            limits={limits}
            usage={usage}
            quote={quote}
            onGoToChecklist={() => setTab("checklist")}
          />
        </div>
      </div>
    </div>
  );
}
